import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import type { UseComboboxState, UseComboboxStateChange, UseComboboxStateChangeOptions } from 'downshift'
import { useCombobox } from 'downshift'
import tinyColor from 'tinycolor2'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { getPredictions } from '../../clients/googleMapsClient'
import { fetchPlaceDetail } from '../../store/address/thunks'
import { updateHouseNumber, updateStreet } from '../../store/address/addressSlice'
import { useReportViewEvent, useResponsive } from '../../hooks'
import { Status } from '../../@types/status'
import { enrichLead } from '../../store/lead/thunks'
import ValuationBar from '../../components/ValuationBar/ValuationBar'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { RealEstateType } from '../../@types/realEstateType'
import { BlueHighlighterIcon } from '../../components/icons/BlueHighlighterIcon'
import { Intent } from '../../@types/intent'
import { reportTrackingEvent } from '../../tracking/thunks'
import { StatusMessage } from '../../components/styled/StatusMessage/StatusMessage'
import { TextInput } from '../../components/styled/TextInput/TextInput'
import { Button } from '../../components/styled/CosmaButton/CosmaButton'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import { WizardFlowType } from '../../@types/wizardFlowType'
import { richValuationFlowProgress } from '../../progress/valuationProgressHelper'
import { STEP } from '../../@types/state'
import './AddressStep.scss'

interface AddressStepProps {
  goToNextStep: () => unknown
  goToErrorStep: () => unknown
}

interface Prediction {
  description: string
  placeId: string
}

const intentToMessage = (intent = Intent.BUY): { heading: string; message: string } => {
  switch (intent) {
    case Intent.BUY:
      return {
        heading: 'Anfrage gesendet',
        message: 'Ein passender Experte wird sich bei Dein melden, um eine unverbindliche Beratung durchzuführen.',
      }
    case Intent.RENT:
      return {
        heading: 'Immobilie erfolgreich bewertet',
        message: 'Das Ergebnis erhältst du zusätzlich per E-Mail.',
      }
    case Intent.LET:
    case Intent.MAKLER_LET:
    case Intent.MAYBE_MAKLER_LET:
    case Intent.INDEPENDENT_LET:
      return {
        heading: 'Passende Experten melden sich in Kürze',
        message: 'Du erhältst eine individuelle Beratung rund um deinen Vermietungsprozess.',
      }
    case Intent.MAKLER_SALE:
      return {
        heading: 'Passende Makler melden sich in Kürze',
        message: 'Du erhältst eine individuelle Beratung und kannst den präzisen Wert der Immobilie besprechen.',
      }
    case Intent.MAYBE_MAKLER_SALE:
      return {
        heading: 'Passende Makler melden sich in Kürze',
        message: 'Du erhältst eine individuelle Beratung und kannst den präzisen Wert der Immobilie besprechen.',
      }
    case Intent.INDEPENDENT_SALE:
      return {
        heading: 'Anfrage gesendet',
        message:
          'Ein passender Experte wird sich bei Dein melden, um eine unverbindliche Verkaufsberatung durchzuführen.',
      }
    case Intent.PARTIAL_SALE:
      return {
        heading: 'Anfrage gesendet',
        message:
          'Ein passender Experte wird sich bei Ihnen melden, um ein individuelles und kostenloses Teilverkaufs-Angebot zu erstellen.',
      }
    default:
      return { heading: '', message: '' }
  }
}

export const AddressStep: FC<AddressStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'address' })
  const { isMobile } = useResponsive()
  const houseNumberInputRef = useRef(null)
  const {
    config: { theme, illustration, integrationType },
    flow: { wizardFlowType, nonHappyPath },
    lead: { realEstateType, optionalStreet, optionalHouseNumber, id: leadId, intent },
    location: { data: location },
    address: { status: placeDetailStatus, data: address },
    user: { hasNotSeenContactForm },
  } = useSelector((state: ReduxState) => state)

  const isPdfWordingChangeEnabled: boolean =
    wizardFlowType === WizardFlowType.IBW &&
    (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT)
  const externalIntegration = integrationType === IntegrationType.EXTERNAL
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24
  const valuationBarProgress = richValuationFlowProgress(STEP.ADDRESS)
  console.log('hasNotSeenContactForm', hasNotSeenContactForm)

  if (!location || !location?.coordinates) {
    console.error('Invalid address step props!', props)
    props.goToErrorStep()
  }

  const [streetInputValue, setStreetInputValue] = useState<string | null | undefined>('')
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>(
    new google.maps.places.AutocompleteSessionToken()
  )
  const [streetInputChanged, setStreetInputChanged] = useState<boolean>(false)
  const [houseNumberInputChanged, setHouseNumberInputChanged] = useState<boolean>(false)
  const [isValidStreetInputDelayed, setIsValidStreetInputDelayed] = useState<boolean>(true)
  const [isNextEnabled, setNextEnabled] = useState<boolean>(true)
  const [selectedPrediction, setSelectedPrediction] = useState<Prediction>()

  const dispatch = useDispatch()

  useEffect(() => {
    if (placeDetailStatus === Status.ERROR) {
      props.goToErrorStep()
    }
  }, [placeDetailStatus])

  useEffect(() => {
    if (selectedPrediction) {
      dispatch(fetchPlaceDetail({ placeId: selectedPrediction.placeId, sessionToken: sessionToken }))
    }
  }, [selectedPrediction])

  useEffect(() => {
    if (address?.street) {
      setStreetInputValue(address?.street)
      if (!externalIntegration) {
        setSessionToken(new google.maps.places.AutocompleteSessionToken())
        const houseNumberInput: HTMLInputElement = houseNumberInputRef.current as unknown as HTMLInputElement
        houseNumberInput.focus()
      }
    }
  }, [address?.street])

  const getAutocompletePredictions = (input?: string | null) => {
    if (!input || externalIntegration) {
      setPredictions([])
      return Promise.resolve()
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return getPredictions(input, location!.coordinates!, sessionToken)
      .then((response) => {
        setPredictions(response)
        return response
      })
      .catch((error) => {
        console.error(error)
        props.goToErrorStep()
      })
  }

  const delayedGetAutoCompletePredictions = useCallback(debounce(getAutocompletePredictions, 250), [])

  const stateReducer = (
    state: UseComboboxState<Prediction>,
    actionAndChanges: UseComboboxStateChangeOptions<Prediction>
  ) => {
    const { type, changes } = actionAndChanges
    switch (type) {
      case useCombobox.stateChangeTypes.InputBlur:
        if (predictions.length > 0) {
          setSelectedPrediction(predictions[0])
        }
        break
      case useCombobox.stateChangeTypes.InputChange:
        if (Object.prototype.hasOwnProperty.call(changes, 'inputValue')) {
          setStreetInputChanged(true)
          setStreetInputValue(changes.inputValue)
          if (!externalIntegration) {
            delayedGetAutoCompletePredictions(changes.inputValue)
          } else {
            dispatch(updateStreet(changes.inputValue))
          }
        }
    }
    return changes
  }

  const onSelectedItemChange = (change: UseComboboxStateChange<Prediction>) => {
    if (!change.selectedItem) return
    setSelectedPrediction(change.selectedItem)
  }

  const { getItemProps, getInputProps, getMenuProps, isOpen, highlightedIndex, getComboboxProps } = useCombobox({
    items: predictions,
    itemToString: (item) => (item ? item.description : ''),
    stateReducer: stateReducer,
    inputValue: streetInputValue || '',
    onSelectedItemChange: onSelectedItemChange,
  })

  const isValidHouseNumber = !!address?.houseNumber && address.houseNumber !== '0'
  const isValidHouseNumberInput = !houseNumberInputChanged || isValidHouseNumber

  const isValidStreet = !!address?.street && address.street === streetInputValue
  const isValidStreetInput = !streetInputChanged || (isOpen && predictions.length > 0) || isValidStreet

  const throttledIsValidStreetInputDelayed = useRef(
    debounce((valid) => setIsValidStreetInputDelayed(valid), 1000)
  ).current

  useEffect(() => {
    throttledIsValidStreetInputDelayed.cancel()
    isValidStreetInput ? setIsValidStreetInputDelayed(true) : throttledIsValidStreetInputDelayed(isValidStreetInput)
  }, [isValidStreetInput])

  useEffect(() => {
    if (optionalStreet) {
      getAutocompletePredictions(`${optionalStreet} ${optionalHouseNumber}`).then((predictions) => {
        if (predictions && predictions.length > 0) {
          setSelectedPrediction(predictions[0])
          setStreetInputValue(optionalStreet)
        }
      })
    }
  }, [optionalStreet])

  const onHouseNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHouseNumberInputChanged(true)
    dispatch(updateHouseNumber(event.target.value))
  }

  const determineEnrichLeadValuationType = (realEstateType?: RealEstateType) => {
    if (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT) {
      return EnrichLeadValuationType.RANGE
    } else if (realEstateType === RealEstateType.LAND) {
      return EnrichLeadValuationType.SINGLE_VALUATION
    }
  }

  const handleInvalidInputs = () => {
    dispatch(
      reportTrackingEvent({
        pageTitle: 'address',
        label: 'error_address_invalid-data',
        inputValue: 'invalid_address',
        eventName: 'lead_error',
        customDimensions: {
          ga_cd_lead_engine_input_value: `${streetInputValue ? streetInputValue : undefined} ${
            address?.houseNumber ? address?.houseNumber : undefined
          }`,
        },
      })
    )
    setIsValidStreetInputDelayed(isValidStreet)
    setHouseNumberInputChanged(true)
  }

  const onNextClick = () => {
    if (!hasNotSeenContactForm && leadId === undefined) {
      console.error('Lead id prop does not exist')
      props.goToErrorStep()
      return
    }
    if (!isValidStreet || !isValidHouseNumber) {
      handleInvalidInputs()
      return
    }
    const rangeType = determineEnrichLeadValuationType(realEstateType)

    setNextEnabled(false)

    dispatch(
      reportTrackingEvent({
        pageTitle: 'address',
        label: 'address_submit',
        inputValue: `${address?.street} ${address?.houseNumber}`,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: `${address?.street} ${address?.houseNumber}`,
        },
      })
    )

    if (hasNotSeenContactForm) {
      props.goToNextStep()
    } else {
      dispatch(
        enrichLead({
          pageTitle: 'address',
          enrichValuationType: rangeType,
          valuationForStep: STEP.CONSTRUCTION_YEAR,
          request: {
            id: leadId ? leadId : '',
            address: `${address?.street} ${address?.houseNumber}`,
          },
          successCallback: () => {
            props.goToNextStep()
          },
          errorCallback: () => {
            props.goToNextStep()
          },
        })
      )
    }
  }
  const isBuyLetRentOrSellIntent = (intent?: Intent) =>
    intent &&
    [
      Intent.BUY,
      Intent.LET,
      Intent.RENT,
      Intent.MAKLER_SALE,
      Intent.INDEPENDENT_SALE,
      Intent.MAYBE_MAKLER_SALE,
      Intent.INDEPENDENT_LET,
    ].includes(intent)

  const isSellIntent = (intent?: Intent) =>
    intent && [Intent.MAKLER_SALE, Intent.INDEPENDENT_SALE, Intent.MAYBE_MAKLER_SALE].includes(intent)

  const ctaText = () => {
    if (nonHappyPath) return 'Bewertung generieren'

    if (isSellIntent(intent)) {
      if (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT) {
        return 'Immobilienwert verfeinern'
      }

      if (realEstateType === RealEstateType.LAND) {
        return 'Grundstückswert verfeinern'
      }
    }

    return isPdfWordingChangeEnabled ? 'Bewertung generieren' : 'Bewertungs-PDF generieren'
  }

  const showPdfTeaser =
    !isIv24Integration &&
    isBuyLetRentOrSellIntent(intent) &&
    (realEstateType === RealEstateType.HOUSE || realEstateType === RealEstateType.APARTMENT)

  const headline = nonHappyPath ? '' : 'Dein ungefährer Immobilienwert'

  const { heading: statusMessageHeadline, message: statusMessageBody } = intentToMessage(intent)

  const header = nonHappyPath ? (
    ''
  ) : (
    <div
      className={`status-message-container margin-bottom-xl padding-horizontal-l ${
        isIv24Integration ? 'iv24StatusMessageScopedCssFix' : ''
      }`}
      data-testid='status-message'
    >
      <StatusMessage
        headline={statusMessageHeadline}
        statusType='confirm'
        statusStyle={'toast'}
        message={isMobile ? '' : statusMessageBody}
        textColor={theme.text}
        borderColor={theme.primary}
        backgroundColor={theme.background}
      />
    </div>
  )
  return (
    <StepTemplate
      header={header}
      headline={headline}
      headlineClassname={isMobile ? 'font-m font-bold' : ''}
      displayNext={false}
      displayBack={false}
      backgroundColor={theme.background}
      textColor={theme.text}
      secondaryLight={theme.secondaryLight}
      illus={illustration}
    >
      <div className='address-container' data-testid='address-container'>
        <div className='grid address-wrapper'>
          <div
            className='grid one-whole margin-bottom-xxl address-valuation-bar-container'
            data-testid='address-valuation-bar-container'
          >
            <ValuationBar progress={valuationBarProgress} valuationAmountClass={` font-m`} largeValuationBar={true} />
          </div>

          {showPdfTeaser && (
            <div className='address-pdf-teaser' data-testid='pdf-teaser-container'>
              <div className='grid-flex gutter-s grid-align-center grid-justify-center'>
                <div className='grid-item '>
                  <div className='icons'>
                    <BlueHighlighterIcon />
                    <i className='font-l is24-icon-pdf' />
                  </div>
                </div>

                <div className='grid-item '>
                  <h6 className='teaser-headline'>
                    {nonHappyPath
                      ? 'Bewertung zum Download'
                      : isPdfWordingChangeEnabled
                      ? 'Bewertung zum Download'
                      : 'Bewertungs-PDF'}
                  </h6>
                </div>
              </div>

              <div className='grid-flex gutter-s grid-justify-center'>
                <div className='teaser-message' data-testid='teaser-message'>
                  {nonHappyPath
                    ? 'Um deine detaillierte Bewertung zu erstellen, benötigen wir noch einige Angaben von dir.'
                    : isPdfWordingChangeEnabled
                    ? 'Um deine detaillierte Bewertung zu erstellen, benötigen wir noch einige Angaben von dir.'
                    : 'Damit das Bewertungs-PDF erstellt werden kann, benötigen wir noch einige Angaben von dir.'}
                </div>
              </div>
            </div>
          )}

          <div className='grid one-whole margin-bottom-xxl' />
          <div className='grid-item two-thirds'>
            <div {...getComboboxProps()} className='grid-item one-whole'>
              <TextInput
                {...getInputProps()}
                id='street-input'
                data-testid='street-input'
                label='Straße'
                placeholder='z.B. Invalidenstraße'
                success={isValidStreet}
                valid={externalIntegration ? typeof address?.street !== 'undefined' : isValidStreetInputDelayed}
                data-success={isValidStreet}
                data-valid={isValidStreetInputDelayed}
                autoFocus
                textColor={theme.text}
                borderColor={theme.secondary}
                backgroundColor={theme.background}
                className={'input-text'}
              />
            </div>
          </div>
          <div className='grid-item one-third padding-left-m'>
            <TextInput
              id='house-number-input'
              data-testid='house-number-input'
              placeholder='z.B. 65'
              label='Nr.'
              maxLength={5}
              onChange={onHouseNumberChange}
              value={address?.houseNumber || ''}
              valid={externalIntegration ? typeof address?.houseNumber !== 'undefined' : isValidHouseNumberInput}
              data-valid={isValidHouseNumberInput}
              ref={houseNumberInputRef}
              textColor={theme.text}
              borderColor={theme.secondary}
              backgroundColor={theme.background}
              className={'input-text'}
            />
          </div>
          <div {...getMenuProps()} className='grid-item one-whole'>
            {isOpen && predictions.length > 0 && (
              <div className='street-suggestion-menu'>
                {predictions.map((item, index) => (
                  <div
                    {...getItemProps({ index, item })}
                    key={item.placeId}
                    data-testid='street-suggestion-item'
                    className={`grid-item one-whole street-suggestion-item ${
                      highlightedIndex === index ? 'street-suggestion-item-highlighted' : ''
                    }`}
                  >
                    {item.description}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className='grid one-whole margin-bottom-l' />
        <div className='grid'>
          <Button
            className='one-whole'
            appearance='primary'
            onClick={onNextClick}
            data-testid='address-cta'
            disabled={!isNextEnabled}
            primary={theme.primary}
            secondary={theme.secondary}
            textColor={
              externalIntegration && tinyColor(theme?.secondary).isDark()
                ? '#FFFFFF'
                : isIv24Integration && tinyColor(theme?.primary).isDark()
                ? '#FFFFFF'
                : theme.text
            }
            secondaryLight={theme.secondaryLight}
            iv24BackgroundColor={isIv24Integration ? theme.secondary : theme.background}
          >
            {ctaText()}
          </Button>
        </div>
      </div>
    </StepTemplate>
  )
}

export default AddressStep
